<template>
  <TemplateOneColumn :has-page-heading="false">
    <template #column-1>
      <AtomText
        class="mt-6 mb-4"
        :text="t('dashboard.title')"
        font-size="text-2xl"
        font-style="font-semibold"
        data-cy="dashboard-title"
      />

      <div class="mb-4">
        <KeyIndicatorsComponent
          :key-indicators="keyIndicators"
          :time-passed-since-update="getTimePassedSinceUpdate"
          :is-loading="isKeyIndicatorsLoading"
        />
      </div>

      <ExternalLinksComponent />

      <AtomGradientCard
        class="mt-4 lg:w-[456px] xl:w-[596px] 2xl:w-[calc(50%-12px)]"
        data-cy="step-by-step-card"
      >
        <AtomText
          class="mb-2"
          :text="t('dashboard.stepByStep.title')"
          font-size="text-sm"
          font-style="font-semibold"
          color-variant="text-inverse"
        />

        <AtomText
          class="mb-2"
          :text="t('dashboard.stepByStep.description')"
          font-size="text-xl"
          color-variant="text-inverse"
        />

        <MoleculeButton
          variant="tertiary"
          size="md"
          :label="t('dashboard.stepByStep.buttonLabel')"
          icon-name="SupportIcon"
          icon-position="left"
          icon-type="outline"
          @clicked="goToInstallationGuide"
        />
      </AtomGradientCard>
    </template>
  </TemplateOneColumn>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

import { useDashboardStore } from '@/stores';

import { getHoursPassed } from '@/utils/dates.utils';

import ExternalLinksComponent from '@/components/dashboard/ExternalLinks.component.vue';
import KeyIndicatorsComponent from '@/components/dashboard/KeyIndicators.component.vue';

import { INSTALLATION_GUIDE_URL } from '@/constants';

const { t } = useI18n();

const dashboardStore = useDashboardStore();
const { keyIndicators, isKeyIndicatorsLoading } = storeToRefs(useDashboardStore());

const getTimePassedSinceUpdate = computed(() => {
  if (!keyIndicators.value?.lastUpdatedAt) {
    return null;
  }

  const hours = getHoursPassed(new Date(keyIndicators.value?.lastUpdatedAt), new Date());

  if (hours >= 24) {
    return t('dashboard.keyIndicators.lastUpdatedDays', {
      count: Math.floor(hours / 24),
    });
  }

  return t('dashboard.keyIndicators.lastUpdatedHours', {
    count: hours,
  });
});

const goToInstallationGuide = () => {
  if (window.dataLayer) {
    window.dataLayer.push({ event: 'click-home-step-by-step' });
  }
  window.open(INSTALLATION_GUIDE_URL, '_blank');
};

onMounted(async () => {
  await dashboardStore.fetchKeyIndicators();
});
</script>
